import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { useNotification } from './NotificationContext';
import ConfirmationModal from '../common/components/ConfirmationModal';

type ConfirmOptions = {
  title: React.ReactNode;
  description?: React.ReactNode;
  primaryButtonText?: React.ReactNode;
  secondaryButtonText?: React.ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl' | string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
  outerPadding?: number;
  primaryButtonDisabled?: boolean;
  suppressible?: boolean; // non-critical prompts
};

export type ConfirmResult = { confirmed: boolean; suppressed?: boolean };

type ConfirmContextType = {
  confirm: (opts: ConfirmOptions) => Promise<ConfirmResult>;
};

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { showAlerts } = useNotification();
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions>({ title: '' });
  const [resolver, setResolver] = useState<((r: ConfirmResult) => void) | undefined>();

  const confirm = useCallback<ConfirmContextType['confirm']>(
    (options: ConfirmOptions) => {
      if (options.suppressible && !showAlerts) {
        return Promise.resolve({ confirmed: false, suppressed: true });
      }
      setOpts(options);
      setOpen(true);
      return new Promise<ConfirmResult>((resolve) => setResolver(() => resolve));
    },
    [showAlerts]
  );

  const resolveAndClose = (result: ConfirmResult) => {
    resolver?.(result);
    setResolver(undefined);
    setOpen(false);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmationModal
        isOpen={open}
        title={opts.title}
        description={opts.description}
        primaryButtonText={opts.primaryButtonText ?? 'Confirm'}
        secondaryButtonText={opts.secondaryButtonText ?? 'Cancel'}
        onPrimaryAction={() => resolveAndClose({ confirmed: true })}
        onSecondaryAction={() => resolveAndClose({ confirmed: false })}
        onClose={() => resolveAndClose({ confirmed: false })}
        width={opts.width}
        maxWidth={opts.maxWidth}
        outerPadding={opts.outerPadding}
        primaryButtonDisabled={opts.primaryButtonDisabled}
      />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmContextType['confirm'] => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx.confirm;
};
